"use client";
import React, { useRef, useState } from "react";

import { carouselConfigMobile, carouselConfigWeb } from "@stadium/constants";

import { Slider } from "@sendmagic/prismic-nextjs/src/components/utils/SlickSlider";
import PrismicImgix from "@sendmagic/prismic-nextjs/src/components/Imgix";

import { textResolver } from "@sendmagic/prismic-nextjs/src/constants/text-resolver";
import { checkContentPresence } from "@sendmagic/prismic-nextjs/src/helpers/prismic";
import PrismicLinkTag from "@sendmagic/prismic-nextjs/src/components/LinkTag";

import { appendBackgroundImage } from "@sendmagic/prismic-nextjs/src/helpers/images";

import defaultStyles from "./main.module.scss";

const commonSliderSettings = {
  ...carouselConfigMobile,
  slidesToShow: 1,
  slidesToScroll: 1,
  touchMove: true,
  swipeToSlide: true,
  infinite: true,
};

const responseSliderSettings = [
  {
    breakpoint: 992,
    settings: commonSliderSettings,
  },
  {
    breakpoint: 767,
    settings: commonSliderSettings,
  },
];

const carouselConfig = {
  ...carouselConfigWeb,
  infinite: true,
  className: "center",
  centerMode: true,
  centerPadding: "20%",
  slidesToShow: 1,
  slidesToScroll: 1,
  responsive: responseSliderSettings,
  touchMove: false,
};

const ImageLink = ({ image_link: { url, target }, sliceStyles: styles = defaultStyles }) =>
  url ? (
    <PrismicLinkTag url={url} target={target} className={styles.animatedImageSliderSlideLink} />
  ) : (
    <></>
  );

const AnimatedImageSlider = ({ doc, sliceStyles: styles = defaultStyles }) => {
  const slider = useRef(null);
  const [currentSlice, setCurrentSlice] = useState(0);
  const { header, description, bottom_header, bottom_description } = doc?.primary || {};

  const customCarouselConfig = {
    ...carouselConfig,
    afterChange: (current) => setCurrentSlice(current),
  };

  return (
    <div className={styles.animatedImageSliderBannerSection}>
      {(checkContentPresence(header) || checkContentPresence(description)) && (
        <div className={styles.sliceContentContainer}>
          <div className={styles.animatedImageSliderTitleBlock}>
            {checkContentPresence(header) && (
              <div className={styles.animatedImageSliderTitleText}>
                {textResolver(header, { convertLabelToModular: true })}
              </div>
            )}
            {checkContentPresence(description) && (
              <div className={styles.animatedImageSliderDescription}>
                {textResolver(description, { convertLabelToModular: true })}
              </div>
            )}
          </div>
        </div>
      )}
      <div className={styles.animatedImageSliderBlock}>
        <div className={styles.animatedImageSliderContainer}>
          <Slider ref={slider} {...customCarouselConfig} styles={styles}>
            {doc?.items?.map(
              (
                {
                  image: { url: imageUrl, alt: imageAlt },
                  image_link,
                  mobile_image: { url: mobileImageUrl, alt: mobileImageAlt },
                  desktop_scroll_image: { url: scrollImageUrl },
                },
                idx,
              ) => {
                const bgImageStyle = appendBackgroundImage(scrollImageUrl);
                return (
                  <div
                    key={imageUrl}
                    className={`${
                      idx === currentSlice ? styles.slickActive : styles.slickInActive
                    } ${styles.animationImageScroll}`}
                  >
                    <div className={styles.animatedImageSliderSlideImage}>
                      {imageUrl && (
                        <div className={styles.animatedImageSliderDesktopImage}>
                          <PrismicImgix
                            className={styles.slideImage}
                            src={imageUrl}
                            alt={imageAlt}
                          />
                          <div className={styles.scrollingSection} style={bgImageStyle}>
                            <ImageLink image_link={image_link} />
                          </div>
                        </div>
                      )}
                      {mobileImageUrl && (
                        <div className={styles.animatedImageSliderMobileImage}>
                          <PrismicImgix src={mobileImageUrl} alt={mobileImageAlt} />
                          <ImageLink image_link={image_link} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              },
            )}
          </Slider>
        </div>
      </div>
      {(checkContentPresence(bottom_header) || checkContentPresence(bottom_description)) && (
        <div className={styles.animatedImageSliderFooterBlock}>
          {checkContentPresence(bottom_header) && (
            <div className={styles.animatedImageSliderFooterBlockTitle}>
              {textResolver(bottom_header, { convertLabelToModular: true })}
            </div>
          )}
          {checkContentPresence(bottom_description) && (
            <div className={styles.animatedImageSliderFooterBlockDescription}>
              {textResolver(bottom_description, { convertLabelToModular: true })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnimatedImageSlider;
